import React, { useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { X } from "lucide-react";
import { setPreload } from "../../../server/CartsOpen";
import showAlert from "../../../server/ShowAlert";
import {
  AddProductToCategory,
  checkProductExistence,
  updateProductFields,
} from "../../../server/Product";
import NewProductPopUp from "./NewProductPopUp";
import { GTIN_LENGTHS, isGtin } from "../../../utils/barcode";
import {
  DIALOG_STEP,
  getExistingCategory,
  getProductId,
  getProductName,
  isProductInCategorySlug,
  normalizeCheckProductResult,
  resolveCategoryDisplayName,
  getAddProductDialogTitle,
  isNoProductsFoundError,
} from "./addProductToCategoryUtils";
import "./AddProductToCategoryDialog.scss";

const AddProductToCategoryDialog = ({
  isOpen,
  onClose,
  categorySlug,
  categoryName,
  categoryLevel,
  isFamily = false,
  onSuccess,
}) => {
  const dialogTitle = getAddProductDialogTitle({
    categoryName,
    categoryLevel,
    isFamily,
  });
  const dispatch = useDispatch();
  const stationId = useSelector((state) => state.user.stationId);

  const [newMkt, setNewMkt] = useState("");
  const [barcodeError, setBarcodeError] = useState("");
  const [dialogStep, setDialogStep] = useState(DIALOG_STEP.INPUT);
  const [foundProduct, setFoundProduct] = useState(null);
  const [existingCategory, setExistingCategory] = useState(null);
  const [barcodeDialogOpen, setBarcodeDialogOpen] = useState(false);
  const [isCreateModalOpen, setIsCreateModalOpen] = useState(false);

  const resetDialog = () => {
    setDialogStep(DIALOG_STEP.INPUT);
    setFoundProduct(null);
    setExistingCategory(null);
    setNewMkt("");
    setBarcodeError("");
  };

  const closeDialog = () => {
    resetDialog();
    setBarcodeDialogOpen(false);
    setIsCreateModalOpen(false);
    onClose?.();
  };

  useEffect(() => {
    if (isOpen) {
      setBarcodeDialogOpen(true);
      return;
    }
    resetDialog();
    setBarcodeDialogOpen(false);
    setIsCreateModalOpen(false);
    // eslint-disable-next-line react-hooks/exhaustive-deps -- reset when parent closes
  }, [isOpen]);

  const handleCheckAndAdd = async () => {
    if (!categorySlug) return;
    if (!isGtin(newMkt)) {
      setBarcodeError(
        newMkt
          ? "ברקוד לא תקין – יש להזין 8, 12, 13 או 14 ספרות"
          : "יש להזין ברקוד",
      );
      return;
    }
    dispatch(setPreload(true));
    setFoundProduct(null);
    setExistingCategory(null);
    setDialogStep(DIALOG_STEP.INPUT);

    try {
      const checkRes = await checkProductExistence(newMkt, stationId);
      const productData = normalizeCheckProductResult(checkRes.data);

      if (!productData) {
        setDialogStep(DIALOG_STEP.NOT_FOUND);
        return;
      }

      if (isProductInCategorySlug(productData, categorySlug)) {
        setDialogStep(DIALOG_STEP.ALREADY_IN_CATEGORY);
        return;
      }

      const currentCategory = getExistingCategory(productData);
      setFoundProduct(productData);

      if (currentCategory && currentCategory.slug !== categorySlug) {
        const displayName = await resolveCategoryDisplayName(
          stationId,
          currentCategory.slug,
          currentCategory.name,
        );
        setExistingCategory({ ...currentCategory, name: displayName });
        setDialogStep(DIALOG_STEP.CATEGORY_CONFLICT);
      } else {
        setDialogStep(DIALOG_STEP.CONFIRM_ADD);
      }
    } catch (err) {
      if (isNoProductsFoundError(err)) {
        setDialogStep(DIALOG_STEP.NOT_FOUND);
        return;
      }
      showAlert({ msgTitle: "אירעה שגיאה בבדיקה. נסה שוב.", msgType: "error" });
      setDialogStep(DIALOG_STEP.INPUT);
    } finally {
      dispatch(setPreload(false));
    }
  };

  const confirmAddProduct = async () => {
    if (!foundProduct || !categorySlug) return;
    const productId = getProductId(foundProduct);
    if (!productId) {
      showAlert({ msgTitle: "לא ניתן לזהות את המוצר", msgType: "error" });
      return;
    }

    dispatch(setPreload(true));
    try {
      await dispatch(AddProductToCategory(categorySlug, productId, stationId));
    } catch (err) {
      showAlert({
        msgTitle: isNoProductsFoundError(err)
          ? "המוצר לא נמצא בסניף הנוכחי ולכן לא ניתן להוסיף אותו לקטגוריה."
          : "אירעה שגיאה בהוספת המוצר.",
        msgType: "error",
      });
      return;
    } finally {
      dispatch(setPreload(false));
    }
    showAlert({
      msgTitle: "המוצר נוסף לקטגוריה בהצלחה",
      msgType: "success",
    });
    closeDialog();
    onSuccess?.();
  };

  const confirmMoveProduct = async () => {
    if (!foundProduct || !existingCategory || !categorySlug) return;
    const productId = getProductId(foundProduct);
    if (!productId) {
      showAlert({ msgTitle: "לא ניתן לזהות את המוצר", msgType: "error" });
      return;
    }

    dispatch(setPreload(true));
    try {
      await updateProductFields(stationId, productId, {
        fromSlug: existingCategory.slug,
        toSlug: categorySlug,
      });
    } catch (err) {
      showAlert({
        msgTitle: isNoProductsFoundError(err)
          ? "המוצר לא נמצא בסניף הנוכחי ולכן לא ניתן להעביר אותו לקטגוריה."
          : "אירעה שגיאה בהעברת המוצר.",
        msgType: "error",
      });
      return;
    } finally {
      dispatch(setPreload(false));
    }
    showAlert({
      msgTitle: "המוצר הועבר לקטגוריה בהצלחה",
      msgType: "success",
    });
    closeDialog();
    onSuccess?.();
  };

  const renderDialogMessage = () => {
    switch (dialogStep) {
      case DIALOG_STEP.ALREADY_IN_CATEGORY:
        return (
          <p className="dialog-message dialog-message--info">
            המוצר כבר קיים בקטגוריה הנוכחית
          </p>
        );
      case DIALOG_STEP.NOT_FOUND:
        return (
          <p className="dialog-message">
            המוצר לא נמצא במערכת. האם תרצה ליצור אותו?
          </p>
        );
      case DIALOG_STEP.CONFIRM_ADD:
        return (
          <p className="dialog-message">
            האם להוסיף את המוצר &quot;{getProductName(foundProduct)}&quot;
            לקטגוריה &quot;{categoryName}&quot;?
          </p>
        );
      case DIALOG_STEP.CATEGORY_CONFLICT:
        return (
          <p className="dialog-message">
            המוצר &quot;{getProductName(foundProduct)}&quot; שייך כעת לקטגוריה
            &quot;{existingCategory?.name}&quot;.
            <br />
            האם להשאיר אותו בקטגוריה הנוכחית או להעביר לקטגוריה &quot;
            {categoryName}&quot;?
          </p>
        );
      default:
        return null;
    }
  };

  const renderDialogActions = () => {
    switch (dialogStep) {
      case DIALOG_STEP.ALREADY_IN_CATEGORY:
        return (
          <button
            type="button"
            className="category-dialog-btn category-dialog-btn--secondary"
            onClick={resetDialog}
          >
            סגור
          </button>
        );
      case DIALOG_STEP.NOT_FOUND:
        return (
          <div className="category-dialog-actions">
            <button
              type="button"
              className="category-dialog-btn category-dialog-btn--primary"
              onClick={() => {
                setBarcodeDialogOpen(false);
                setIsCreateModalOpen(true);
              }}
            >
              יצירת מוצר חדש
            </button>
            <button
              type="button"
              className="category-dialog-btn category-dialog-btn--secondary"
              onClick={resetDialog}
            >
              ביטול
            </button>
          </div>
        );
      case DIALOG_STEP.CONFIRM_ADD:
        return (
          <div className="category-dialog-actions">
            <button
              type="button"
              className="category-dialog-btn category-dialog-btn--primary"
              onClick={confirmAddProduct}
            >
              הוסף לקטגוריה
            </button>
            <button
              type="button"
              className="category-dialog-btn category-dialog-btn--secondary"
              onClick={resetDialog}
            >
              ביטול
            </button>
          </div>
        );
      case DIALOG_STEP.CATEGORY_CONFLICT:
        return (
          <div className="category-dialog-actions">
            <button
              type="button"
              className="category-dialog-btn category-dialog-btn--primary"
              onClick={confirmMoveProduct}
            >
              העבר לקטגוריה זו
            </button>
            <button
              type="button"
              className="category-dialog-btn category-dialog-btn--secondary"
              onClick={closeDialog}
            >
              השאר בקטגוריה הנוכחית
            </button>
          </div>
        );
      default:
        return (
          <button
            type="button"
            className="category-dialog-btn category-dialog-btn--primary"
            onClick={handleCheckAndAdd}
          >
            הוסף
          </button>
        );
    }
  };

  return (
    <>
      {barcodeDialogOpen ? (
        <div className="add-product-to-category-dialog__overlay">
          <div className="add-product-to-category-dialog__box">
            <button
              type="button"
              className="close-icon close-popup"
              onClick={closeDialog}
              aria-label="סגור"
            >
              <X className="close-popup__icon" size={22} strokeWidth={2} />
            </button>
            <h2 className="add-product-to-category-dialog__title">
              {dialogTitle}
            </h2>
            <input
              type="text"
              inputMode="numeric"
              maxLength={Math.max(...GTIN_LENGTHS)}
              value={newMkt}
              className={barcodeError ? "input-error" : undefined}
              aria-invalid={Boolean(barcodeError)}
              aria-describedby={
                barcodeError ? "add-product-barcode-error" : undefined
              }
              onKeyDown={(e) => {
                if (e.key === "Enter" && dialogStep === DIALOG_STEP.INPUT) {
                  handleCheckAndAdd();
                }
              }}
              onChange={(e) => {
                setNewMkt(e.target.value.replace(/\D/g, ""));
                setBarcodeError("");
                if (dialogStep !== DIALOG_STEP.INPUT) {
                  setDialogStep(DIALOG_STEP.INPUT);
                  setFoundProduct(null);
                  setExistingCategory(null);
                }
              }}
              placeholder="הכנס ברקוד"
              autoFocus
            />
            {barcodeError ? (
              <p
                id="add-product-barcode-error"
                className="dialog-message dialog-message--error"
                role="alert"
              >
                {barcodeError}
              </p>
            ) : null}
            {renderDialogMessage()}
            {renderDialogActions()}
          </div>
        </div>
      ) : null}

      <NewProductPopUp
        isOpen={isCreateModalOpen}
        onClose={closeDialog}
        onCreated={() => {
          showAlert({
            msgTitle: "המוצר נוצר ונוסף בהצלחה",
            msgType: "success",
          });
          onSuccess?.();
        }}
        initialBarcode={newMkt}
        categorySlug={categorySlug}
        categoryName={categoryName ?? ""}
      />
    </>
  );
};

export default AddProductToCategoryDialog;
